import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  CircularProgress,
  DialogActions,
  Stack,
  Box,
  Typography,
  Divider
} from "@mui/material";
import { Form, Formik, FieldArray } from "formik";
import TextFieldWrapper from "../FormComponents/TextFieldWrapper";
import { Cancel, Forward } from "@mui/icons-material";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";
import AlertPopup from "../AlertPopup";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  }
}));

export default function AddEditLLEventModal({ event }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async (data) => {
      return UserQuery.CSEQuery.addLLEvent(data);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["llEvents"]);
        setOpen(false);
      }, 2000);
    }
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return UserQuery.CSEQuery.updateLLEvent(id, data);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["llEvents"]);
        setOpen(false);
      }, 2000);
    }
  });

  const handleToggleOpen = () => {
    setOpen(!open);
  };

  return (
    <div>
      {event ? (
        <IconButton onClick={handleToggleOpen} color="primary" size="small">
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleToggleOpen}
          sx={{
            px: 3,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
              transition: "all 0.3s ease"
            }
          }}
        >
          Add LL Event
        </Button>
      )}

      {addMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={addMutation.error?.response?.data?.message || "Server Error"}
        />
      )}
      {addMutation.isSuccess && (
        <AlertPopup open={true} message={addMutation.data?.message} />
      )}

      {editMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            editMutation.error?.response?.data?.message || "Server Error"
          }
        />
      )}
      {editMutation.isSuccess && (
        <AlertPopup open={true} message={editMutation.data?.message} />
      )}

      <BootstrapDialog
        onClose={handleToggleOpen}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {event ? "Edit LL Event" : "Add LL Event"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleToggleOpen}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Formik
            initialValues={{
              serviceProvider: event?.serviceProvider || "",
              topic: event?.topic || "",
              trainingDates: event?.trainingDates || [{ date: "", time: "" }],
              registrationLink: event?.registrationLink || ""
            }}
            validationSchema={Yup.object().shape({
              serviceProvider: Yup.string().required(
                "Service Provider is required"
              ),
              topic: Yup.string().required("Topic is required"),
              trainingDates: Yup.array()
                .of(
                  Yup.object().shape({
                    date: Yup.string().required("Date is required"),
                    time: Yup.string().required("Time is required")
                  })
                )
                .min(1, "At least one training date is required"),
              registrationLink: Yup.string().url("Must be a valid URL")
            })}
            onSubmit={(values, { setSubmitting }) => {
              const data = {
                serviceProvider: values.serviceProvider,
                topic: values.topic,
                trainingDates: values.trainingDates,
                registrationLink: values.registrationLink
              };

              if (event) {
                editMutation.mutate({ id: event.id, data });
              } else {
                addMutation.mutate(data);
              }

              setSubmitting(false);
            }}
          >
            {({ values, isSubmitting }) => (
              <Form>
                <Stack spacing={3}>
                  <TextFieldWrapper
                    name="serviceProvider"
                    label="Service Provider"
                    placeholder="Enter service provider name"
                    required
                  />

                  <TextFieldWrapper
                    name="topic"
                    label="Topic"
                    placeholder="Enter training topic"
                    required
                  />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Training Dates & Times
                    </Typography>
                    <FieldArray name="trainingDates">
                      {({ push, remove }) => (
                        <Stack spacing={2}>
                          {values.trainingDates.map((_, index) => (
                            <Box key={index}>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="flex-start"
                              >
                                <TextFieldWrapper
                                  name={`trainingDates.${index}.date`}
                                  label={`Date ${index + 1}`}
                                  placeholder="e.g., 4 December 2025"
                                  required
                                  fullWidth
                                />
                                <TextFieldWrapper
                                  name={`trainingDates.${index}.time`}
                                  label={`Time ${index + 1}`}
                                  placeholder="e.g., 10h00 – 12h30"
                                  required
                                  fullWidth
                                />
                                {values.trainingDates.length > 1 && (
                                  <IconButton
                                    color="error"
                                    onClick={() => remove(index)}
                                    sx={{ mt: 1 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Stack>
                              {index < values.trainingDates.length - 1 && (
                                <Divider sx={{ my: 1 }} />
                              )}
                            </Box>
                          ))}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => push({ date: "", time: "" })}
                            sx={{ alignSelf: "flex-start" }}
                          >
                            + Add Another Date
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </Box>

                  <TextFieldWrapper
                    name="registrationLink"
                    label="Registration Link"
                    placeholder="https://example.com/register"
                  />

                  <DialogActions sx={{ px: 0, pb: 0 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleToggleOpen}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Forward />}
                      disabled={
                        isSubmitting ||
                        addMutation.isLoading ||
                        editMutation.isLoading
                      }
                    >
                      {isSubmitting ||
                      addMutation.isLoading ||
                      editMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : event ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </DialogActions>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </BootstrapDialog>
    </div>
  );
}
